import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/api-helpers';
import { Button } from '@ouropix/ui';
import { Card, CardContent } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@ouropix/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import { Switch } from '@ouropix/ui';
import { Separator } from '@ouropix/ui';
import { Checkbox } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ShieldCheck, Grid3x3, Settings, Calendar as CalendarIcon, Trophy, Layers, Play, Loader2, BarChart3 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import AdminPageHeader from '@/components/layout/PageHeader';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

const MODALITY_SLUGS = [
  { value: 'girodeouro', label: 'Giro de Ouro' },
  { value: 'cacapix', label: 'Caça Pix' },
  { value: 'raspouepix', label: 'Raspou É Pix' },
  { value: 'baudotesouro', label: 'Baú do Tesouro' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'expired', label: 'Expirado' },
];

export const Route = createFileRoute('/_admin/lotep/estruturas/')({
  component: LotepEstruturasPage,
});

interface LotepPrize {
  tier: number;
  prize_value_brl: string;
  quantity: number;
  total_value_brl: string;
  prize_catalog_id?: string;
  prize_catalog_name?: string;
  prize_catalog_image_url?: string;
}

interface LotepPoolInfo {
  id: string;
  status: string;
  commitment_hash: string;
  numbers_total: number;
  numbers_consumed: number;
  created_at: string;
}

interface LotepStructure {
  id: string;
  lotep_code: string;
  campaign_name?: string;
  process_lotep_code?: string;
  modality_slug: string;
  modality_slugs?: string[];
  authorized_at: string;
  expires_at?: string;
  final_draw_at?: string;
  reminder_days_before?: number;
  max_pool_value_brl: string;
  max_ticket_value_brl: string;
  total_numbers: number;
  status: string;
  created_at: string;
  prizes: LotepPrize[];
  final_draw_prizes?: LotepPrize[];
  pools?: LotepPoolInfo[];
}

interface PrizeTierRow {
  tier: number;
  prize_value_brl: string;
  quantity: string;
  prize_catalog_id: string;
}

interface ModalityGameConfig {
  enabled: boolean;
  theme_id: string;
  featured: boolean;
  play_price_brl: string;
}

type ModalityKey = 'girodeouro' | 'cacapix' | 'raspouepix' | 'baudotesouro';

const DEFAULT_PRIZE: PrizeTierRow = { tier: 1, prize_value_brl: '', quantity: '1', prize_catalog_id: '' };

interface FormData {
  // Config
  lotep_code: string;
  campaign_name: string;
  process_lotep_code: string;
  modality_slug: string;
  // Datas
  authorized_at: string;
  expires_at: string;
  reminder_days_before: string;
  final_draw_at: string;
  // Financeiro — tetos são derivados no submit (preço da jogada por modalidade),
  // não pedidos ao usuário. Único limite editável é o total de bilhetes.
  total_numbers: string;
  // Prêmios Lúdicos
  prizes: PrizeTierRow[];
  // Prêmios Sorteio Final
  final_draw_prizes: PrizeTierRow[];
  // Modalidades + Jogos
  modality_games: Record<ModalityKey, ModalityGameConfig>;
  auto_create_pool: boolean;
}

const DEFAULT_MODALITY_GAMES: Record<ModalityKey, ModalityGameConfig> = {
  girodeouro: { enabled: false, theme_id: '', featured: false, play_price_brl: '0.10' },
  cacapix: { enabled: false, theme_id: '', featured: false, play_price_brl: '0.10' },
  raspouepix: { enabled: false, theme_id: '', featured: false, play_price_brl: '0.10' },
  baudotesouro: { enabled: false, theme_id: '', featured: false, play_price_brl: '0.10' },
};

const DEFAULT_FORM: FormData = {
  lotep_code: '',
  campaign_name: '',
  process_lotep_code: '',
  modality_slug: '',
  authorized_at: '',
  expires_at: '',
  reminder_days_before: '7',
  final_draw_at: '',
  total_numbers: '',
  prizes: [{ ...DEFAULT_PRIZE }],
  final_draw_prizes: [],
  modality_games: { ...DEFAULT_MODALITY_GAMES },
  auto_create_pool: true,
};

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'active' ? 'default' : status === 'suspended' ? 'secondary' : 'outline';
  const label = STATUS_OPTIONS.find(s => s.value === status)?.label ?? status;
  return <Badge variant={variant}>{label}</Badge>;
}

interface StructureStats {
  structure_id: string;
  lotep_code: string;
  campaign_name: string;
  modality_slug: string;
  structure_status: string;
  total_numbers: number;
  numbers_consumed: number;
  pool_status: string;
  exhausted_at: string | null;
  revealed_at: string | null;
  plays: number;
  unique_players: number;
  revenue_brl: string;
  prizes_won_cash: number;
  prizes_paid_brl: string;
  prizes_won_physical: number;
  final_draw_winners: number;
  final_draw_at: string | null;
  first_play_at: string | null;
  last_play_at: string | null;
  winners: DrawWinner[];
}

interface DrawWinner {
  winner_name: string;
  play_number: number;
  prize_name: string;
  prize_value_brl: string;
  prize_image_url: string;
  drawn_at: string | null;
  position: number;
}

function StatCard({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold ${className ?? ''}`}>{value}</p>
    </div>
  );
}

function StructureStatsModal({ structure, onClose }: { structure: LotepStructure | null; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery<StructureStats>({
    queryKey: ['admin-lotep-structure-stats', structure?.id],
    queryFn: async () => {
      const r = await api.get(`/admin/lotep/structures/${structure!.id}/stats`);
      return r.data as StructureStats;
    },
    enabled: !!structure,
  });

  const fmtBRL = (v: string | number) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-BR') : '—');
  const pctSold = data && data.total_numbers > 0 ? Math.round((data.numbers_consumed / data.total_numbers) * 100) : 0;
  const revenue = Number(data?.revenue_brl || 0);
  const prizesPaid = Number(data?.prizes_paid_brl || 0);
  const profit = revenue - prizesPaid;

  return (
    <Dialog open={!!structure} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Estatísticas — {structure?.campaign_name || structure?.lotep_code}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Carregando...
          </div>
        ) : isError || !data ? (
          <div className="py-10 text-center text-destructive">Erro ao carregar estatísticas.</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Receita" value={fmtBRL(revenue)} className="text-emerald-600" />
              <StatCard label="Prêmios pagos" value={fmtBRL(prizesPaid)} className="text-amber-600" />
              <StatCard label="Resultado" value={fmtBRL(profit)} className={profit >= 0 ? 'text-emerald-600' : 'text-destructive'} />
              <StatCard label="Jogadores únicos" value={data.unique_players.toLocaleString('pt-BR')} />
              <StatCard label="Jogadas" value={data.plays.toLocaleString('pt-BR')} />
              <StatCard label="% vendido" value={`${pctSold}% (${data.numbers_consumed}/${data.total_numbers})`} />
              <StatCard label="Prêmios em dinheiro" value={data.prizes_won_cash.toLocaleString('pt-BR')} />
              <StatCard label="Prêmios físicos" value={data.prizes_won_physical.toLocaleString('pt-BR')} />
            </div>
            <Separator />
            <div>
              <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                <Trophy className="h-4 w-4 text-amber-500" />
                Sorteio final ({data.final_draw_winners} ganhador{data.final_draw_winners === 1 ? '' : 'es'})
              </p>
              {data.winners.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sorteio final ainda não realizado.</p>
              ) : (
                <div className="space-y-2">
                  {data.winners.map((w, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg border p-2">
                      {w.prize_image_url ? (
                        <img src={w.prize_image_url} alt={w.prize_name} className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                          <Trophy className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{w.winner_name || 'Sem ganhador'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {w.prize_name}{w.play_number ? ` · bilhete #${w.play_number}` : ''}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-amber-600 whitespace-nowrap">{fmtBRL(w.prize_value_brl)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Separator />
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Encerramento (pool)</span><span>{fmtDate(data.revealed_at || data.exhausted_at)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Primeira jogada</span><span>{fmtDate(data.first_play_at)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Última jogada</span><span>{fmtDate(data.last_play_at)}</span></div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PrizeTierTable({
  prizes,
  disabled,
  prizesCatalog,
  onChange,
  onAdd,
  onRemove,
  onMove,
  showCatalogErrors,
}: {
  prizes: PrizeTierRow[];
  disabled: boolean;
  prizesCatalog: Array<{ id: string; name: string; value: string }> | undefined;
  onChange: (index: number, field: keyof PrizeTierRow, value: string | number) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMove?: (index: number, dir: -1 | 1) => void;
  showCatalogErrors?: boolean;
}) {
  const catalogValueOf = (id: string) => {
    const raw = (prizesCatalog ?? []).find(c => c.id === id)?.value;
    const n = Number(raw ?? 0);
    return Number.isFinite(n)
      ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Premiações</Label>
        {!disabled && (
          <Button type="button" variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-3 w-3 mr-1" /> Prêmio
          </Button>
        )}
      </div>
      {prizes.map((prize, i) => {
        const missingCatalog = showCatalogErrors && !disabled && !prize.prize_catalog_id;
        return (
          <div key={i} className="grid grid-cols-4 gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">
                Ordem{onMove ? ` · ${i + 1}º sorteado` : ''}
              </Label>
              <div className="flex gap-1">
                <div className="flex h-9 flex-1 items-center rounded-md border bg-muted px-3 text-sm">
                  {i + 1}
                </div>
                {onMove && !disabled && prizes.length > 1 && (
                  <div className="flex flex-col shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-[18px] w-6"
                      disabled={i === 0}
                      aria-label="Mover prêmio para cima (sortear antes)"
                      onClick={() => onMove(i, -1)}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-[18px] w-6"
                      disabled={i === prizes.length - 1}
                      aria-label="Mover prêmio para baixo (sortear depois)"
                      onClick={() => onMove(i, 1)}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Valor</Label>
              <div className="flex h-9 items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground">
                {prize.prize_catalog_id ? catalogValueOf(prize.prize_catalog_id) : '—'}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Qtd</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={prize.quantity}
                onChange={e => onChange(i, 'quantity', e.target.value.replace(/\D/g, ''))}
                placeholder="1"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Prêmio (biblioteca) *
              </Label>
              <div className="flex gap-1">
                <Select
                  value={prize.prize_catalog_id || ''}
                  onValueChange={v => onChange(i, 'prize_catalog_id', v)}
                  disabled={disabled}
                >
                  <SelectTrigger className={`text-xs${missingCatalog ? ' border-destructive ring-1 ring-destructive' : ''}`}>
                    <SelectValue placeholder="Selecione um prêmio *" />
                  </SelectTrigger>
                  <SelectContent>
                    {(prizesCatalog ?? []).map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!disabled && prizes.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => onRemove(i)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {missingCatalog && (
                <p className="text-xs text-destructive">Selecione um prêmio do catálogo</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LotepEstruturasPage() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState<LotepStructure | null>(null);
  const [structureToDelete, setStructureToDelete] = useState<LotepStructure | null>(null);
  // Estrutura que a exclusão normal recusou por ter pool vinculada → confirmar force-delete
  const [structureToForceDelete, setStructureToForceDelete] = useState<LotepStructure | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
  const [showCatalogErrors, setShowCatalogErrors] = useState(false);
  const [activeTab, setActiveTab] = useState<'andamento' | 'finalizados'>('andamento');
  const [statsStructure, setStatsStructure] = useState<LotepStructure | null>(null);

  const { data, isLoading } = useQuery<LotepStructure[]>({
    queryKey: ['admin-lotep-structures', filterStatus, filterModality],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterModality) params.set('modality_slug', filterModality);
      const r = await api.get(`/admin/lotep/structures?${params}`);
      return (r.data?.structures ?? []) as LotepStructure[];
    },
  });

  const { data: prizesCatalog } = useQuery<Array<{ id: string; name: string; value: string; image_url?: string; prize_type?: string }>>({
    queryKey: ['admin-prizes-catalog'],
    queryFn: async () => {
      const r = await api.get('/admin/prizes');
      const d = r.data;
      if (Array.isArray(d?.prizes)) return d.prizes;
      if (Array.isArray(d?.data)) return d.data;
      return Array.isArray(d) ? d : [];
    },
  });

  // Jogos rascunho vinculados às estruturas (pra publicar sem sair desta página).
  const { data: lotepGames } = useQuery<Array<{ id: string; use_lotep_pool?: boolean; status?: string; title?: string; pool_generated?: boolean; lotep_structure_id?: string; play_price?: number; modality?: { code?: string }; theme?: { id?: string } }>>({
    queryKey: ['admin-games-registered'],
    queryFn: async () => {
      const r = await api.get('/admin/games', { params: { status: 'draft,scheduled', limit: 500 } });
      const d = r.data;
      if (Array.isArray(d?.games)) return d.games;
      if (Array.isArray(d?.data)) return d.data;
      return Array.isArray(d) ? d : [];
    },
  });

  const [publishingStructureId, setPublishingStructureId] = useState<string | null>(null);

  // Publica todos os rascunhos de uma estrutura (generate-pool → validate → active).
  // Mantém o gate deliberado de publicação (compliance LOTEP), sem trocar de página.
  const handlePublishStructure = async (structureId: string, gameIds: string[]) => {
    if (publishingStructureId || gameIds.length === 0) return;
    setPublishingStructureId(structureId);
    let ok = 0;
    try {
      for (const gameId of gameIds) {
        const g = (lotepGames ?? []).find(x => x.id === gameId);
        if (!g?.pool_generated) {
          const poolData = await api.post<{ success: boolean; error?: string }>(`/admin/games/${gameId}/generate-pool`);
          if (!poolData.data.success) throw new Error(poolData.data.error || 'Falha na geração da pool');
        }
        const validation = await api.post<{ valid: boolean; errors: string[] }>(`/admin/games/${gameId}/validate-pool`);
        if (!validation.data.valid) throw new Error(validation.data.errors.join('; '));
        await api.patch(`/admin/games/${gameId}`, { status: 'active', start_at: new Date().toISOString() });
        ok += 1;
      }
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      queryClient.invalidateQueries({ queryKey: ['admin-lotep-structures'] });
      toast.success(`Estrutura publicada (${ok}/${gameIds.length} jogos).`);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, `Erro ao publicar estrutura (${ok}/${gameIds.length} publicados)`));
    } finally {
      setPublishingStructureId(null);
    }
  };

  const { data: themesAll } = useQuery<Array<{ id: string; name: string; modalityCode?: string }>>({
    queryKey: ['admin-themes-all'],
    queryFn: async () => {
      const r = await api.get('/admin/game-themes');
      const d = r.data;
      if (Array.isArray(d?.themes)) return d.themes;
      if (Array.isArray(d?.data)) return d.data;
      return Array.isArray(d) ? d : [];
    },
  });

  // Monta o payload da estrutura (reusado por create + update). Valor do prêmio vem
  // da biblioteca; tetos LOTEP são derivados; ordem/tier = posição visual das linhas.
  const buildStructurePayload = (data: FormData) => {
    const enabledModalities = MODALITY_SLUGS
      .map(m => m.value as ModalityKey)
      .filter(key => data.modality_games[key].enabled)
      .map(key => ({
        modality_code: key,
        enabled: true,
        theme_id: data.modality_games[key].theme_id || '',
        featured: data.modality_games[key].featured,
        play_price_brl: data.modality_games[key].play_price_brl || '0.10',
      }));

    // Tetos LOTEP derivados: teto de bilhete = maior preço de jogada; teto de pool =
    // total de bilhetes × teto de bilhete. Backend exige ambos NOT NULL.
    const ticketPrices = enabledModalities.map(m => Number(m.play_price_brl) || 0);
    const maxTicketValue = ticketPrices.length ? Math.max(...ticketPrices) : 0;
    const maxPoolValue = (Number(data.total_numbers) || 0) * maxTicketValue;

    return {
      lotep_code: data.lotep_code,
      campaign_name: data.campaign_name || undefined,
      process_lotep_code: data.process_lotep_code || undefined,
      modality_slug: data.modality_slug,
      authorized_at: data.authorized_at,
      expires_at: data.expires_at || undefined,
      final_draw_at: data.final_draw_at || undefined,
      reminder_days_before: data.reminder_days_before ? Number(data.reminder_days_before) : 7,
      max_pool_value_brl: maxPoolValue.toFixed(2),
      max_ticket_value_brl: maxTicketValue.toFixed(2),
      total_numbers: Number(data.total_numbers),
      prizes: data.prizes.map((p, i) => ({
        ...p,
        tier: i + 1,
        prize_value_brl: p.prize_catalog_id ? String(catalogValue(p.prize_catalog_id)) : p.prize_value_brl,
        quantity: Number(p.quantity),
      })),
      final_draw_prizes: data.final_draw_prizes.map((p, i) => ({
        ...p,
        tier: i + 1,
        prize_value_brl: p.prize_catalog_id ? String(catalogValue(p.prize_catalog_id)) : p.prize_value_brl,
        quantity: Number(p.quantity),
        position: i + 1, // ordem de sorteio = ordem visual das linhas (ordem≠tier)
      })),
      auto_create_pool: data.auto_create_pool,
      modalities: enabledModalities,
    };
  };

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const r = await api.post('/admin/lotep/structures', buildStructurePayload(data));
      return r.data;
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-lotep-structures'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      const gameCount = (result?.created_game_ids ?? result?.createdGameIds)?.length ?? 0;
      const hasPool = !!(result?.created_pool_id ?? result?.createdPoolId);
      const gameErrors: string[] = result?.game_errors ?? result?.gameErrors ?? [];
      const expected = result?.expected_game_count ?? result?.expectedGameCount ?? 0;
      // Algum jogo não foi criado (ex.: bilhetes fora da faixa 100-1M): avisa o
      // operador em vez de deixar a estrutura órfã passar como sucesso silencioso.
      if (gameErrors.length > 0 || (expected > 0 && gameCount < expected)) {
        toast.error(
          `Estrutura criada, mas ${expected - gameCount} de ${expected} jogo(s) NÃO foram criados: ${gameErrors.join(' | ')}`,
          { duration: 12000 },
        );
        handleCloseForm();
        return;
      }
      if (gameCount > 0 || hasPool) {
        const parts = [
          'Estrutura LOTEP criada.',
          gameCount > 0 && `${gameCount} jogo${gameCount > 1 ? 's' : ''} rascunho${gameCount > 1 ? 's' : ''} criado${gameCount > 1 ? 's' : ''}.`,
          hasPool && 'Pool aberta.',
        ].filter(Boolean).join(' ');
        toast.success(parts, {
          action: { label: 'Ver Jogos', onClick: () => { window.location.href = '/jogos'; } },
        });
      } else {
        toast.success('Estrutura LOTEP criada!');
      }
      handleCloseForm();
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Erro ao criar estrutura')),
  });

  // Edição inline de estrutura NÃO publicada (regenera pool + jogos draft no backend).
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormData }) => {
      const r = await api.patch(`/admin/lotep/structures/${id}`, buildStructurePayload(data));
      return r.data;
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-lotep-structures'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      const gameCount = (result?.created_game_ids ?? result?.createdGameIds)?.length ?? 0;
      const gameErrors: string[] = result?.game_errors ?? result?.gameErrors ?? [];
      const expected = result?.expected_game_count ?? result?.expectedGameCount ?? 0;
      if (gameErrors.length > 0 || (expected > 0 && gameCount < expected)) {
        toast.error(
          `Estrutura atualizada, mas ${expected - gameCount} de ${expected} jogo(s) NÃO foram criados: ${gameErrors.join(' | ')}`,
          { duration: 12000 },
        );
        handleCloseForm();
        return;
      }
      toast.success('Estrutura atualizada! Pool regenerada e jogos recriados.');
      handleCloseForm();
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Erro ao atualizar estrutura')),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/admin/lotep/structures/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lotep-structures'] });
      toast.success('Status atualizado!');
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Erro ao atualizar status')),
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }) => {
      await api.delete(`/admin/lotep/structures/${id}${force ? '?force=true' : ''}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-lotep-structures'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      toast.success('Estrutura excluída!');
      setStructureToDelete(null);
      if (variables.force) setStructureToForceDelete(null);
    },
    onError: (error: unknown, variables) => {
      // Exclusão normal recusada por pool vinculada → oferecer force (só superadmin)
      const msg = apiErrorMessage(error, 'Erro ao excluir');
      if (!variables.force && /pool\(s\) associada/i.test(msg)) {
        const target = structureToDelete;
        setStructureToDelete(null);
        setStructureToForceDelete(target);
        return;
      }
      toast.error(msg);
    },
  });

  const handleOpenForm = (structure?: LotepStructure) => {
    if (structure) {
      setSelectedStructure(structure);
      // Pré-popula as modalidades da estrutura. Tema/preço não vivem no objeto
      // structure — ficam nos jogos (game-catalog). Recupera-os dos jogos da
      // estrutura (casados por lotep_structure_id + modalidade) pra NÃO perder o
      // tema selecionado ao reeditar/salvar.
      const enabledSlugs = new Set(structure.modality_slugs ?? [structure.modality_slug]);
      const gamesByModality = new Map<string, { theme_id: string; play_price_brl: string }>();
      for (const g of lotepGames ?? []) {
        const code = g.modality?.code;
        if (g.lotep_structure_id === structure.id && code) {
          gamesByModality.set(code, {
            theme_id: g.theme?.id ?? '',
            play_price_brl: g.play_price != null ? String(g.play_price) : '',
          });
        }
      }
      const modalityGames = { ...DEFAULT_MODALITY_GAMES };
      (Object.keys(modalityGames) as ModalityKey[]).forEach(key => {
        const existing = gamesByModality.get(key);
        modalityGames[key] = {
          ...modalityGames[key],
          enabled: enabledSlugs.has(key),
          ...(existing?.theme_id ? { theme_id: existing.theme_id } : {}),
          ...(existing?.play_price_brl ? { play_price_brl: existing.play_price_brl } : {}),
        };
      });
      setFormData({
        lotep_code: structure.lotep_code,
        campaign_name: structure.campaign_name ?? '',
        process_lotep_code: structure.process_lotep_code ?? '',
        modality_slug: structure.modality_slug,
        authorized_at: structure.authorized_at ? structure.authorized_at.slice(0, 10) : '',
        expires_at: structure.expires_at ? structure.expires_at.slice(0, 10) : '',
        reminder_days_before: String(structure.reminder_days_before ?? 7),
        final_draw_at: structure.final_draw_at ? structure.final_draw_at.slice(0, 16) : '',
        total_numbers: String(structure.total_numbers),
        prizes: structure.prizes.length > 0
          ? structure.prizes.map(p => ({
              tier: p.tier,
              prize_value_brl: p.prize_value_brl,
              quantity: String(p.quantity),
              prize_catalog_id: p.prize_catalog_id ?? '',
            }))
          : [{ ...DEFAULT_PRIZE }],
        final_draw_prizes: (structure.final_draw_prizes ?? []).map(p => ({
          tier: p.tier,
          prize_value_brl: p.prize_value_brl,
          quantity: String(p.quantity),
          prize_catalog_id: p.prize_catalog_id ?? '',
        })),
        modality_games: modalityGames,
        auto_create_pool: true,
      });
    } else {
      setSelectedStructure(null);
      setFormData({ ...DEFAULT_FORM, modality_games: { ...DEFAULT_MODALITY_GAMES } });
    }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedStructure(null);
    setShowCatalogErrors(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.lotep_code || !formData.authorized_at || !formData.total_numbers) {
      toast.error('Preencha todos os campos obrigatórios na aba "Configurações"');
      return;
    }
    // Sem teto/piso artificial de bilhetes — admin define livremente. Só exige ≥ 1.
    const numBilhetes = Number(formData.total_numbers);
    if (!Number.isFinite(numBilhetes) || numBilhetes < 1) {
      toast.error('Total de bilhetes deve ser um número maior ou igual a 1.');
      return;
    }
    // Modalidade principal = primeira modality habilitada na aba "Modalidades + Jogos"
    const firstEnabled = MODALITY_SLUGS
      .map(m => m.value as ModalityKey)
      .find(key => formData.modality_games[key].enabled);
    if (!firstEnabled) {
      toast.error('Habilite ao menos uma modalidade na aba "Modalidades + Jogos"');
      return;
    }
    if (selectedStructure && !isStructureEditable) {
      toast.info('Estrutura já tem bilhetes jogados — imutável. Use "Alterar Status".');
      return;
    }
    if (prizesOverflow) {
      toast.error(`Prêmios lúdicos (${instantPrizeQty}) excedem o total de bilhetes (${totalNumbers}). Não é possível cadastrar mais prêmios que bilhetes.`);
      return;
    }
    const missingLudicos = formData.prizes.some(p => !p.prize_catalog_id);
    const missingFinal = formData.final_draw_prizes.some(p => !p.prize_catalog_id);
    if (missingLudicos || missingFinal) {
      setShowCatalogErrors(true);
      const where = missingLudicos && missingFinal
        ? 'Prêmios Lúdicos e Prêmios Sorteio Final'
        : missingLudicos ? 'Prêmios Lúdicos' : 'Prêmios Sorteio Final';
      toast.error(`Vincule um prêmio do catálogo em cada tier (${where}). Cadastre em /biblioteca-de-premios.`);
      return;
    }
    const payloadData = { ...formData, modality_slug: firstEnabled };
    if (selectedStructure) {
      updateMutation.mutate({ id: selectedStructure.id, data: payloadData });
    } else {
      createMutation.mutate(payloadData);
    }
  };

  // Prêmios Lúdicos helpers
  const addPrize = () => setFormData(p => ({
    ...p,
    prizes: [...p.prizes, { tier: p.prizes.length + 1, prize_value_brl: '', quantity: '1', prize_catalog_id: '' }],
  }));
  const removePrize = (index: number) => setFormData(p => ({ ...p, prizes: p.prizes.filter((_, i) => i !== index) }));
  const updatePrize = (index: number, field: keyof PrizeTierRow, value: string | number) => {
    setFormData(p => {
      const prizes = [...p.prizes];
      prizes[index] = { ...prizes[index], [field]: value };
      return { ...p, prizes };
    });
  };

  // Prêmios Sorteio Final helpers
  const addFinalPrize = () => setFormData(p => ({
    ...p,
    final_draw_prizes: [...p.final_draw_prizes, { tier: p.final_draw_prizes.length + 1, prize_value_brl: '', quantity: '1', prize_catalog_id: '' }],
  }));
  const removeFinalPrize = (index: number) => setFormData(p => ({ ...p, final_draw_prizes: p.final_draw_prizes.filter((_, i) => i !== index) }));
  const updateFinalPrize = (index: number, field: keyof PrizeTierRow, value: string | number) => {
    setFormData(p => {
      const final_draw_prizes = [...p.final_draw_prizes];
      final_draw_prizes[index] = { ...final_draw_prizes[index], [field]: value };
      return { ...p, final_draw_prizes };
    });
  };
  // Ordem de sorteio (position) = ordem visual das linhas; reorder via ↑/↓.
  const moveFinalPrize = (index: number, dir: -1 | 1) => setFormData(p => {
    const arr = [...p.final_draw_prizes];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    return { ...p, final_draw_prizes: arr };
  });

  // Modalidades + Jogos helpers
  const updateModalityGame = (key: ModalityKey, patch: Partial<ModalityGameConfig>) => {
    setFormData(p => ({
      ...p,
      modality_games: {
        ...p.modality_games,
        [key]: { ...p.modality_games[key], ...patch },
      },
    }));
  };

  // Custo esperado + distribuição de bilhetes.
  // Valor vem da biblioteca (catálogo) quando vinculado; senão usa o valor digitado.
  // Prêmios lúdicos consomem bilhetes do pool; o sorteio final é sorteado entre
  // participantes (não ocupa bilhete), então só os lúdicos contam pra distribuição.
  const catalogValue = (id: string) => Number((prizesCatalog ?? []).find(c => c.id === id)?.value ?? 0);
  const rowValue = (r: PrizeTierRow) => (r.prize_catalog_id ? catalogValue(r.prize_catalog_id) : Number(r.prize_value_brl) || 0);
  const totalNumbers = Number(formData.total_numbers) || 0;
  const instantPrizeQty = formData.prizes.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
  const emptyTickets = Math.max(0, totalNumbers - instantPrizeQty);
  const prizesOverflow = instantPrizeQty > totalNumbers;
  const instantCost = formData.prizes.reduce((s, p) => s + rowValue(p) * (Number(p.quantity) || 0), 0);
  const drawCost = formData.final_draw_prizes.reduce((s, p) => s + rowValue(p) * (Number(p.quantity) || 0), 0);
  const totalCost = instantCost + drawCost;
  const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // "Finalizada" = pool esgotada/revelada (mesmo sinal do sold_out no site).
  const isFinalized = (s: LotepStructure) =>
    (s.pools ?? []).some(p => p.status === 'exhausted' || p.status === 'revealed');
  const allStructures = data ?? [];
  const emAndamento = allStructures.filter(s => !isFinalized(s));
  const finalizadas = allStructures.filter(isFinalized);
  const structures = activeTab === 'finalizados' ? finalizadas : emAndamento;
  const totalPages = Math.ceil(structures.length / PAGE_SIZE);
  const paginated = structures.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  // Edição liberada só enquanto NÃO publicada = pool sem consumo (ninguém jogou).
  // Backend ainda reforça (jogos precisam ser draft) e regenera a pool ao salvar.
  const selectedConsumed = (selectedStructure?.pools ?? []).reduce((sum, p) => sum + (p.numbers_consumed ?? 0), 0);
  const isStructureEditable = !!selectedStructure && selectedConsumed === 0;
  const isReadonly = !!selectedStructure && !isStructureEditable;
  const hasMissingCatalog = !isReadonly && (
    formData.prizes.some(p => !p.prize_catalog_id) ||
    formData.final_draw_prizes.some(p => !p.prize_catalog_id)
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={ShieldCheck}
        title="Estrutura LOTEP"
        description="Autorizações LOTEP por modalidade — base para pools provably-fair"
        actions={
          <Button onClick={() => handleOpenForm()}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Estrutura
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={v => { setActiveTab(v as 'andamento' | 'finalizados'); setCurrentPage(1); }}>
        <TabsList>
          <TabsTrigger value="andamento">Em Andamento ({emAndamento.length})</TabsTrigger>
          <TabsTrigger value="finalizados">Finalizados ({finalizadas.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex gap-3 flex-wrap">
        <Select value={filterModality || '_all'} onValueChange={v => { setFilterModality(v === '_all' ? '' : v); setCurrentPage(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Todas as modalidades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todas as modalidades</SelectItem>
            {MODALITY_SLUGS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus || '_all'} onValueChange={v => { setFilterStatus(v === '_all' ? '' : v); setCurrentPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos os status</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código LOTEP</TableHead>
                  <TableHead>Modalidade</TableHead>
                  <TableHead className="hidden md:table-cell">Autorizado em</TableHead>
                  <TableHead className="hidden sm:table-cell">Bilhetes</TableHead>
                  <TableHead>Jogos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">Carregando...</TableCell>
                  </TableRow>
                ) : structures.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {activeTab === 'finalizados'
                        ? 'Nenhuma estrutura finalizada ainda.'
                        : 'Nenhuma estrutura LOTEP em andamento.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map(s => {
                    // Casa jogos ↔ estrutura por ID (lotep_structure_id), não por
                    // título — os jogos são titulados pela campanha, não pelo lotep_code.
                    const draftGameIds = (lotepGames ?? [])
                      .filter(g => g.use_lotep_pool && g.status === 'draft' && g.lotep_structure_id === s.id)
                      .map(g => g.id);
                    const pool = (s.pools ?? []).find(p => p.status === 'open') ?? (s.pools ?? [])[0];
                    const consumed = pool?.numbers_consumed ?? 0;
                    const poolTotal = pool?.numbers_total ?? s.total_numbers ?? 0;
                    const pct = poolTotal > 0 ? Math.round((consumed / poolTotal) * 100) : 0;
                    return (
                    <TableRow key={s.id}>
                      <TableCell>
                        <code className="bg-muted px-2 py-1 rounded text-xs">{s.lotep_code}</code>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(s.modality_slugs ?? [s.modality_slug]).map(slug => (
                            <Badge key={slug} variant="secondary" className="text-xs whitespace-nowrap">
                              {MODALITY_SLUGS.find(m => m.value === slug)?.label ?? slug}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {s.authorized_at ? new Date(s.authorized_at).toLocaleDateString('pt-BR') : '—'}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{s.total_numbers.toLocaleString('pt-BR')}</TableCell>
                      <TableCell>
                        {pool ? (
                          <div className="space-y-1 min-w-[110px]">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{consumed.toLocaleString('pt-BR')}</span>
                              <span>{poolTotal.toLocaleString('pt-BR')}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            {draftGameIds.length > 0 && (
                              <p className="text-xs text-amber-600">{draftGameIds.length} rascunho(s)</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sem pool</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isFinalized(s) ? (
                          <Badge variant="outline" className="border-emerald-600/40 text-emerald-600">Finalizado</Badge>
                        ) : (
                          <Select
                            value={s.status}
                            onValueChange={status => statusMutation.mutate({ id: s.id, status })}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue>
                                <StatusBadge status={s.status} />
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {draftGameIds.length > 0 && (
                            <Button
                              size="sm"
                              onClick={() => handlePublishStructure(s.id, draftGameIds)}
                              disabled={publishingStructureId === s.id}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            >
                              {publishingStructureId === s.id ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3 mr-1" />
                              )}
                              Publicar ({draftGameIds.length})
                            </Button>
                          )}
                          {(s.pools ?? []).find(p => p.status === 'open') && (
                            <Link
                              to="/lotep/estruturas/$structureId/pool"
                              params={{ structureId: s.id }}
                              search={{ pool_id: (s.pools ?? []).find(p => p.status === 'open')!.id }}
                              aria-label="Visualizar Pool"
                            >
                              <Button variant="ghost" size="icon" className="text-emerald-600 hover:text-emerald-700">
                                <Grid3x3 className="h-4 w-4" />
                              </Button>
                            </Link>
                          )}
                          {isFinalized(s) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setStatsStructure(s)}
                              className="text-primary hover:text-primary"
                              aria-label="Estatísticas"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleOpenForm(s)} aria-label="Ver detalhes">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setStructureToDelete(s)}
                            className="text-destructive hover:text-destructive"
                            aria-label="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">
                {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, structures.length)} de {structures.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs px-2 text-muted-foreground">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Próxima">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <StructureStatsModal
        structure={statsStructure}
        onClose={() => setStatsStructure(null)}
      />

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
          // Form de múltiplas abas: não fecha por clique-fora/foco-fora. O
          // double-click num Select (portalado) fazia o 2º pointerdown vazar
          // pro backdrop do Dialog, que o Radix lê como "interação fora" e
          // fechava o modal — allow-list por target não cobre o overlay. Fecha
          // só via X/Cancelar/submit (Escape segue ativo).
          // Ref: reference_radix_select_dialog_close.md
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{selectedStructure ? 'Detalhes da Estrutura LOTEP' : 'Nova Estrutura LOTEP'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="config" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-4">
                <TabsTrigger value="config" className="flex items-center gap-1.5 text-xs">
                  <Settings className="h-3.5 w-3.5" />
                  Config
                </TabsTrigger>
                <TabsTrigger value="datas" className="flex items-center gap-1.5 text-xs">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Datas
                </TabsTrigger>
                <TabsTrigger value="premios-ludicos" className="flex items-center gap-1.5 text-xs">
                  <Trophy className="h-3.5 w-3.5" />
                  P. Lúdicos
                </TabsTrigger>
                <TabsTrigger value="premios-sorteio" className="flex items-center gap-1.5 text-xs">
                  <Trophy className="h-3.5 w-3.5" />
                  P. Sorteio
                </TabsTrigger>
                <TabsTrigger value="modalidades" className="flex items-center gap-1.5 text-xs">
                  <Layers className="h-3.5 w-3.5" />
                  Modalidades
                </TabsTrigger>
              </TabsList>

              {/* ──────── a) Config ──────── */}
              <TabsContent value="config" className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lotep-code">Código LOTEP *</Label>
                    <Input
                      id="lotep-code"
                      value={formData.lotep_code}
                      onChange={e => setFormData(p => ({ ...p, lotep_code: e.target.value }))}
                      placeholder="LTP-PRC-2026-01724"
                      disabled={isReadonly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="process-lotep-code">Nome Processo LOTEP</Label>
                    <Input
                      id="process-lotep-code"
                      value={formData.process_lotep_code}
                      onChange={e => setFormData(p => ({ ...p, process_lotep_code: e.target.value }))}
                      placeholder="Ex: Raspadinha Natal 2026"
                      disabled={isReadonly}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="campaign-name">Nome da campanha</Label>
                  <Input
                    id="campaign-name"
                    value={formData.campaign_name}
                    onChange={e => setFormData(p => ({ ...p, campaign_name: e.target.value }))}
                    placeholder="Ex: iPhone 17 Pro Max"
                    disabled={isReadonly}
                  />
                  <p className="text-xs text-muted-foreground">
                    Segue no nome dos jogos ("Baú do Tesouro – iPhone 17 Pro Max") e no título do bloco em /sorteios. Sem isso, os jogos ficam só com o nome da modalidade.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total-numbers">Limite de Bilhetes *</Label>
                  <Input
                    id="total-numbers"
                    type="number"
                    min={1}
                    value={formData.total_numbers}
                    onChange={e => setFormData(p => ({ ...p, total_numbers: e.target.value }))}
                    placeholder="100000"
                    disabled={isReadonly}
                  />
                  <p className="text-xs text-muted-foreground">
                    Único limite da estrutura. O preço do bilhete é o preço da jogada configurado em cada modalidade.
                  </p>
                </div>
              </TabsContent>

              {/* ──────── b) Datas ──────── */}
              <TabsContent value="datas" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="authorized-at">Data de Autorização *</Label>
                  <Input
                    id="authorized-at"
                    type="date"
                    value={formData.authorized_at}
                    onChange={e => setFormData(p => ({ ...p, authorized_at: e.target.value }))}
                    disabled={isReadonly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expires-at">Data de Expiração</Label>
                  <Input
                    id="expires-at"
                    type="date"
                    value={formData.expires_at}
                    onChange={e => setFormData(p => ({ ...p, expires_at: e.target.value }))}
                    disabled={isReadonly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reminder-days">Dias de Antecedência (lembrete)</Label>
                  <Input
                    id="reminder-days"
                    type="number"
                    min="1"
                    value={formData.reminder_days_before}
                    onChange={e => setFormData(p => ({ ...p, reminder_days_before: e.target.value }))}
                    placeholder="7"
                    disabled={isReadonly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="final-draw-at">Data e Hora do Sorteio Final (opcional)</Label>
                  <Input
                    id="final-draw-at"
                    type="datetime-local"
                    value={formData.final_draw_at}
                    onChange={e => setFormData(p => ({ ...p, final_draw_at: e.target.value }))}
                    disabled={isReadonly}
                  />
                  <p className="text-xs text-muted-foreground">
                    Define o momento exato do sorteio final (data + horário).
                  </p>
                </div>
              </TabsContent>

              {/* ──────── c) Prêmios Lúdicos ──────── */}
              <TabsContent value="premios-ludicos" className="space-y-4">
                <PrizeTierTable
                  prizes={formData.prizes}
                  disabled={isReadonly}
                  prizesCatalog={prizesCatalog}
                  onChange={updatePrize}
                  onAdd={addPrize}
                  onRemove={removePrize}
                  showCatalogErrors={showCatalogErrors}
                />

                {/* Distribuição de bilhetes + custo esperado */}
                <div className={`rounded-lg border p-3 space-y-2 text-sm${prizesOverflow ? ' border-destructive' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Distribuição de bilhetes</span>
                    <span className="font-medium">
                      {instantPrizeQty.toLocaleString('pt-BR')} prêmios em {totalNumbers.toLocaleString('pt-BR')} bilhetes → {emptyTickets.toLocaleString('pt-BR')} vazios
                    </span>
                  </div>
                  {prizesOverflow && (
                    <p className="text-xs text-destructive">
                      Prêmios lúdicos excedem o total de bilhetes. Reduza a quantidade ou aumente o total de números.
                    </p>
                  )}
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Custo prêmios lúdicos</span>
                    <span className="font-medium">{fmtBRL(instantCost)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Custo prêmio(s) sorteio final</span>
                    <span className="font-medium">{fmtBRL(drawCost)}</span>
                  </div>
                  <div className="flex items-center justify-between text-base">
                    <span className="font-semibold">Custo total esperado</span>
                    <span className="font-semibold">{fmtBRL(totalCost)}</span>
                  </div>
                </div>
              </TabsContent>

              {/* ──────── d) Prêmios Sorteio Final ──────── */}
              <TabsContent value="premios-sorteio" className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Prêmios do sorteio final (físicos tipicamente — PS5, Monitor, etc.). Separados dos prêmios lúdicos instantâneos.
                </p>
                {formData.final_draw_prizes.length === 0 && !isReadonly && (
                  <div className="text-center text-sm text-muted-foreground py-6 border-2 border-dashed rounded-lg">
                    Nenhum prêmio de sorteio final. Clique em "+ Tier" para adicionar.
                  </div>
                )}
                <PrizeTierTable
                  prizes={formData.final_draw_prizes}
                  disabled={isReadonly}
                  prizesCatalog={prizesCatalog}
                  onChange={updateFinalPrize}
                  onAdd={addFinalPrize}
                  onRemove={removeFinalPrize}
                  onMove={moveFinalPrize}
                  showCatalogErrors={showCatalogErrors}
                />
              </TabsContent>

              {/* ──────── e) Modalidades + Jogos ──────── */}
              <TabsContent value="modalidades" className="space-y-4">
                {!isReadonly && (
                  <div className="flex items-center gap-3 rounded-md border px-3 py-2.5">
                    <Checkbox
                      id="auto-create-pool"
                      checked={formData.auto_create_pool}
                      onCheckedChange={v => setFormData(p => ({ ...p, auto_create_pool: v === true }))}
                    />
                    <div>
                      <Label htmlFor="auto-create-pool" className="cursor-pointer font-medium text-sm">
                        Criar pool automaticamente após salvar
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Cria pool aberta + 1 jogo rascunho por modalidade habilitada
                      </p>
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  {MODALITY_SLUGS.map(modality => {
                    const key = modality.value as ModalityKey;
                    const config = formData.modality_games[key];
                    // Só os temas da própria modalidade (cada modalidade tem o seu).
                    const themesForModality = (themesAll ?? []).filter(
                      t => t.modalityCode === key,
                    );
                    return (
                      <div key={key} className="border rounded-lg p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-medium">{modality.label}</Label>
                          <Switch
                            checked={config.enabled}
                            onCheckedChange={v => updateModalityGame(key, v
                              ? { enabled: true, theme_id: config.theme_id || themesForModality[0]?.id || '' }
                              : { enabled: false })}
                            disabled={isReadonly}
                          />
                        </div>
                        {config.enabled && (
                          <div className="grid grid-cols-3 gap-3 animate-in fade-in slide-in-from-top-1 duration-150">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Tema</Label>
                              <Select
                                value={config.theme_id || '_none'}
                                onValueChange={v => updateModalityGame(key, { theme_id: v === '_none' ? '' : v })}
                                disabled={isReadonly}
                              >
                                <SelectTrigger className="text-xs h-8">
                                  <SelectValue placeholder="Sem tema" />
                                </SelectTrigger>
                                <SelectContent>
                                  {themesForModality.length === 0 && (
                                    <SelectItem value="_none">Sem tema (padrão)</SelectItem>
                                  )}
                                  {themesForModality.map(t => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">Valor por número (R$)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                className="h-8 text-xs"
                                value={config.play_price_brl}
                                onChange={e => updateModalityGame(key, { play_price_brl: e.target.value })}
                                placeholder="0.10"
                                disabled={isReadonly}
                              />
                            </div>
                            <div className="flex items-center gap-2 pt-5">
                              <Switch
                                id={`featured-${key}`}
                                checked={config.featured}
                                onCheckedChange={v => updateModalityGame(key, { featured: v })}
                                disabled={isReadonly}
                              />
                              <Label htmlFor={`featured-${key}`} className="text-xs cursor-pointer">Destaque</Label>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>

            {!isReadonly && (
              <DialogFooter className="mt-4 pt-4 border-t">
                {selectedStructure && (
                  <p className="mr-auto self-center text-xs text-amber-600">
                    Salvar regenera a pool (novo sorteio) e recria os jogos rascunho.
                  </p>
                )}
                <Button type="button" variant="outline" onClick={handleCloseForm}>Cancelar</Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending || hasMissingCatalog}>
                  {selectedStructure
                    ? (updateMutation.isPending ? 'Salvando...' : 'Salvar Alterações')
                    : (createMutation.isPending ? 'Criando...' : 'Criar Estrutura')}
                </Button>
              </DialogFooter>
            )}
            {isReadonly && (
              <DialogFooter className="mt-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseForm}>Fechar</Button>
              </DialogFooter>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={!!structureToDelete}
        onClose={() => setStructureToDelete(null)}
        onConfirm={() => { if (structureToDelete) deleteMutation.mutate({ id: structureToDelete.id }); }}
        title="Confirmar Exclusão"
        description={`Excluir estrutura "${structureToDelete?.lotep_code}"? Esta ação é irreversível e falhará se houver pools vinculadas.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
      />

      <ConfirmModal
        isOpen={!!structureToForceDelete}
        onClose={() => setStructureToForceDelete(null)}
        onConfirm={() => { if (structureToForceDelete) deleteMutation.mutate({ id: structureToForceDelete.id, force: true }); }}
        title="Estrutura possui pool vinculada"
        description={`A estrutura "${structureToForceDelete?.lotep_code}" tem pool(s) vinculada(s). Excluir mesmo assim apaga em cascata a pool, os jogos (inclusive publicados/ativos) e os dados de jogadas/ganhadores vinculados. Ação irreversível — use só para limpeza de estruturas de teste.`}
        confirmText="Excluir mesmo assim"
        cancelText="Cancelar"
        variant="destructive"
      />
    </div>
  );
}
